import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LayoutItemProps } from "apis";
import { PageItemProps } from "apis/page";
import { PageBuilderItemProps, PageBuilderModuleItemProps } from "apis/page-builder";
import { uuid } from "utils";
import { PaginationProps } from "utils/types/API";

interface BuilderApiItemProps {
    method?: "get" | "post" | "put" | "delete";
    url?: string;
}
export interface PageBuilderState {
    uid: string;
    loading: boolean;
    page?: PageItemProps;
    builder?: {
        layout?: LayoutItemProps;
        modules?: PageBuilderModuleItemProps[];
    };
    history: PageBuilderItemProps[];
    saved?: boolean;
    _pagination?: PaginationProps;
    _selectModule?: PageBuilderModuleItemProps;
    _tab?: 1 | 2 | 3 | 4;
    _showPropertiesTab?: boolean;
    _data?: { [key: string]: any };
}

const initialState: PageBuilderState = {
    uid: uuid(),
    loading: false,
    history: [],
    _tab: 1,
    _data: {}
};

interface IndexProps {
    data: PageBuilderItemProps[];
    refresh?: boolean;
    pagination?: PaginationProps;
}

interface LayoutChangeProps {
    layout: LayoutItemProps;
}
interface UpdateModules {
    // uid: string;
    modules: PageBuilderModuleItemProps[]
}

export const pageBuilderSlice = createSlice({
    name: "page-builder",
    initialState,
    reducers: {
        loading: (state) => {
            state.loading = true;
        },
        loadPreview: (state, action: PayloadAction<PageBuilderState>) => {
            state.loading = action.payload.loading;
            state.page = action.payload.page;
            state.builder = action.payload.builder;
            state.history = action.payload.history;
            state.saved = action.payload.saved;
            state._pagination = action.payload._pagination;
            state._selectModule = action.payload._selectModule;
            state._showPropertiesTab = action.payload._showPropertiesTab;
            state._tab = action.payload._tab;
            state._data = action.payload._data;
        },
        init: (state, action: PayloadAction<PageBuilderState>) => {
            if (state.page?.id && action.payload.page?.id === state.page?.id) {
                state.uid = uuid();
                state.saved = true;
            }
            state.loading = false;
            state.page = action.payload.page;
            state.builder = action.payload.builder;
            state.history = action.payload.history;
            state._tab = action.payload.builder?.layout ? 2 : 1;
            state._showPropertiesTab = !action.payload.builder?.layout ? true : state._showPropertiesTab;
            state._pagination = action.payload._pagination;
        },
        destroy: (state) => {
            state.loading = false;
            state.page = undefined;
            state.builder = undefined;
            state.history = [];
            state.saved = undefined;
            state._pagination = undefined;
            state._selectModule = undefined;
            state._showPropertiesTab = undefined;
            state._tab = undefined;
            state._data = {};
        },
        layoutChange: (state, action: PayloadAction<LayoutChangeProps>) => {
            state.builder = { ...state.builder, layout: action.payload.layout };
            state.saved = false;
        },
        history: (state, action: PayloadAction<IndexProps>) => {
            state._pagination = action.payload.pagination;
            if (action.payload.refresh) {
                state.history = action.payload.data;
            }
            else {
                state.history = [...state.history, ...action.payload.data];
            }
        },
        addModule: (state, action: PayloadAction<PageBuilderModuleItemProps>) => {
            state.builder = {
                ...state.builder,
                modules: [
                    ...(state.builder?.modules || []),
                    action.payload
                ].sort((a, b) => a.index - b.index)
            }
            state.saved = false;
        },
        updateModules: (state, action: PayloadAction<UpdateModules>) => {
            state.builder = {
                ...state.builder,
                modules: [
                    ...action.payload.modules
                ].sort((a, b) => a.index - b.index),
            }
            state._selectModule = state._selectModule && action.payload.modules.findIndex(t => t.itemUid === state._selectModule?.itemUid) >= 0 ? state._selectModule : undefined;
            state.saved = false;
        },
        removeModule: (state, action: PayloadAction<PageBuilderModuleItemProps>) => {
            if (state._selectModule?.itemUid === action.payload.itemUid) {
                state._selectModule = undefined;
            }
            state.builder = {
                ...state.builder,
                modules: (state.builder?.modules || [])
                    .filter(temp => temp.itemUid !== action.payload.itemUid)
                    .map((temp, index) => ({ ...temp, index }))
                    .sort((a, b) => a.index - b.index)
            }
            state._selectModule = state._selectModule && (state.builder?.modules || []).findIndex(t => t.itemUid === state._selectModule?.itemUid) >= 0 ? state._selectModule : undefined;
            state.saved = false;
        },
        selectModule: (state, action: PayloadAction<PageBuilderModuleItemProps>) => {
            if (Boolean(state._selectModule) && state._selectModule?.itemUid === action.payload.itemUid) {
                state._selectModule = undefined;
            }
            else {
                state._selectModule = action.payload;
                state._tab = 3;
                state._showPropertiesTab = true;
            }
        },
        updateSelectModule: (state, action: PayloadAction<PageBuilderModuleItemProps>) => {
            state.builder = {
                ...state.builder,
                modules: (state.builder?.modules || []).map(temp => {
                    if (temp.itemUid === action.payload.itemUid) {
                        return action.payload;
                    }
                    return temp;
                }).sort((a, b) => a.index - b.index),
            }
            // state._selectModule = state._selectModule && action.payload.modules.findIndex(t => t.itemUid === state._selectModule?.itemUid) >= 0 ? state._selectModule : undefined;
            state.saved = false;

            state._selectModule = action.payload;
        },
        onChangeTab: (state, action: PayloadAction<1 | 2 | 3 | 4>) => {
            state._tab = action.payload;
            state._showPropertiesTab = true;
        },
        onShowPropertiesTab: (state, action: PayloadAction<boolean>) => {
            state._showPropertiesTab = action.payload;
        }
    },
});

export const { loading, init, destroy, history, layoutChange, addModule, updateModules, removeModule, selectModule, updateSelectModule, onChangeTab, onShowPropertiesTab, loadPreview } = pageBuilderSlice.actions;
export default pageBuilderSlice.reducer;
